import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController } from '@ionic/angular/lazy';
import { Router } from '@angular/router';
import { QuoteService } from 'src/app/services/quote.service';
import { CartService } from 'src/app/services/cart.service';
import { ObserverService } from 'src/app/services/observer.service';
import { ParkedCartService } from "../../services/parked-cart.service";
import { DateTimePickerPage } from 'src/app/modals/date-time-picker/date-time-picker.page';
import { StorageService } from 'src/app/services/storage.service';
import { Decimal } from 'decimal.js';
import moment from 'moment-timezone';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-cart-quote',
    templateUrl: './cart-quote.page.html',
    styleUrls: ['./cart-quote.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class CartQuotePage implements OnInit {

    cart = null;
    quote = null;
    spinner = null;
    loading_spinner = null;
    loading = false;
    processing_quote = false;
    online = true;
    moment = moment;
    input_focused = false;
    params = {};
    formatted_expiry_date = null;
    modal_css_class = '';
    date_modal_css_class = '';

    // modal flags
    date_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public loadingController: LoadingController,
        public alertController: AlertController,
        private observerService: ObserverService,
        private cartService: CartService,
        private quoteService: QuoteService,
        private parkedCartService: ParkedCartService,
        private storageService: StorageService,
        private router: Router
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
            this.date_modal_css_class = 'backdrop';
        }

        this.loadData().then(() => {

            if(this.cart.parked_cart_id) {
                this.parkedCartService.getParkedCart(this.cart.parked_cart_id).then((parked_cart) => {
                    if(parked_cart && parked_cart['label']) {
                        this.params["reference"] = parked_cart['label'];
                    }
                })
            }

            if(this.cart.currency) {
                this.params["currency_id"] = this.cart.currency.id;
            }
            if(this.cart.pricing_tier) {
                this.params["pricing_tier_id"] = this.cart.pricing_tier.id;
            }
            if(this.cart.tax_zone) {
                this.params["tax_zone_id"] = this.cart.tax_zone.id;
            }

            if(!this.cart.quote_id) {

                this.storageService.get('quote_default_expiry_days').then((quote_default_expiry_days) => {
                    if(quote_default_expiry_days) {
                        this.params['expiry_date'] = this.moment().add(quote_default_expiry_days, 'days').format("YYYY-MM-DDTHH:mm");
                        this.formatted_expiry_date = this.moment(this.params['expiry_date']).format("D MMM YYYY, HH:mm");
                    }
                });

                if(this.cart.person) {
                    this.params["person_id"] = this.cart.person.id;
                }
                if(this.cart.organisation) {
                    this.params["organisation_id"] = this.cart.organisation.id;
                }

                if(this.cart.special_instructions) {
                    this.params["special_instructions"] = this.cart.special_instructions;
                }
                if(this.cart.customer_note) {
                    this.params['customer_note'] = this.cart.customer_note;
                }
                if(this.cart.internal_note) {
                    this.params['internal_note'] = this.cart.internal_note;
                }

                if(this.cart.shipping_address) {

                    if(this.cart.shipping_address.first_name) {
                        this.params['shipping_address_first_name'] = this.cart.shipping_address.first_name;
                    }
                    if(this.cart.shipping_address.last_name) {
                        this.params['shipping_address_last_name'] = this.cart.shipping_address.last_name;
                    }
                    if(this.cart.shipping_address.organisation_title) {
                        this.params['shipping_address_organisation_title'] = this.cart.shipping_address.organisation_title;
                    }
                    if(this.cart.shipping_address.premises) {
                        this.params['shipping_address_premises'] = this.cart.shipping_address.premises;
                    }
                    if(this.cart.shipping_address.line_1) {
                        this.params['shipping_address_line_1'] = this.cart.shipping_address.line_1;
                    }
                    if(this.cart.shipping_address.line_2) {
                        this.params['shipping_address_line_2'] = this.cart.shipping_address.line_2;
                    }
                    if(this.cart.shipping_address.locality) {
                        this.params['shipping_address_locality'] = this.cart.shipping_address.locality;
                    }
                    if(this.cart.shipping_address.subdivision) {
                        this.params['shipping_address_subdivision'] = this.cart.shipping_address.subdivision;
                    }
                    if(this.cart.shipping_address.postal_code) {
                        this.params['shipping_address_postal_code'] = this.cart.shipping_address.postal_code;
                    }
                    if(this.cart.shipping_address.country_id) {
                        this.params['shipping_address_country_id'] = this.cart.shipping_address.country_id;
                    }
                    if(this.cart.shipping_address.mobile_phone_number) {
                        this.params['shipping_address_mobile_phone_number'] = this.cart.shipping_address.mobile_phone_number;
                    }
                    if(this.cart.shipping_address.what3words_address) {
                        this.params['shipping_address_what3words_address'] = this.cart.shipping_address.what3words_address;
                    }
                    if(this.cart.shipping_address.courier_instructions) {
                        this.params['shipping_address_courier_instructions'] = this.cart.shipping_address.courier_instructions;
                    }

                }

                if(this.cart.shipping_notification_email_address) {
                    this.params['shipping_notification_email_address'] = this.cart.shipping_notification_email_address;
                }

                if(this.cart.shipping) {
                    this.params['shipping_price'] = this.cart.shipping.price;
                    this.params['shipping_discount_rate'] = 0;
                }

                if(this.cart.items) {

                    for(let i = 0; i < this.cart.items.length; i++) {

                        let item = this.cart.items[i];

                        let number = i + 1;

                        if(item.type == 'product') { // product
                            this.params[`item_${number}_product_id`] = item.id
                        }
                        else if(item.type == 'product_variant') { // product variant
                            this.params[`item_${number}_product_variant_id`] = item.id
                        }

                        this.params[`item_${number}_quantity`] = item.quantity
                        this.params[`item_${number}_sell_price`] = item.sell_price
                        this.params[`item_${number}_has_custom_price`] = item.has_custom_price;
                        this.params[`item_${number}_discount_rate`] = item.discount_rate;

                        if(item.customer_note) {
                            this.params[`item_${number}_customer_note`] = item.customer_note
                        }

                        if(item.internal_note) {
                            this.params[`item_${number}_internal_note`] = item.internal_note
                        }

                    }
                }

            }
            else {

                this.params["update_items"] = true;
                this.params["reference"] = "";
                this.params["expiry_date"] = "";
                this.params["person_id"] = "";
                this.params["organisation_id"] = "";
                this.params["special_instructions"] = "";
                this.params['customer_note'] = "";
                this.params['internal_note'] = "";
                this.params['shipping_address_first_name'] = "";
                this.params['shipping_address_last_name'] = "";
                this.params['shipping_address_organisation_title'] = "";
                this.params['shipping_address_premises'] = "";
                this.params['shipping_address_line_1'] = "";
                this.params['shipping_address_line_2'] = "";
                this.params['shipping_address_locality'] = "";
                this.params['shipping_address_subdivision'] = "";
                this.params['shipping_address_postal_code'] = "";
                this.params['shipping_address_country_id'] = "";
                this.params['shipping_address_mobile_phone_number'] = "";
                this.params['shipping_address_what3words_address'] = "";
                this.params['shipping_address_courier_instructions'] = "";
                this.params['shipping_notification_email_address'] = "";
                this.params['shipping_price'] = "";
                this.params['shipping_discount_rate'] = "";

                this.loadQuote().then(() => {

                    if(this.quote.reference) {
                        this.params["reference"] = this.quote.reference;
                    }

                    if(this.quote.expiry_date) {
                        this.params["expiry_date"] = this.quote.expiry_date;
                        this.formatted_expiry_date = this.moment(this.quote.expiry_date).format("D MMM YYYY, HH:mm");
                    }

                    if(this.cart.person) {
                        this.params["person_id"] = this.cart.person.id;
                    }
                    if(this.cart.organisation) {
                        this.params["organisation_id"] = this.cart.organisation.id;
                    }

                    if(this.cart.special_instructions) {
                        this.params["special_instructions"] = this.cart.special_instructions;
                    }
                    if(this.cart.customer_note) {
                        this.params['customer_note'] = this.cart.customer_note;
                    }
                    if(this.cart.internal_note) {
                        this.params['internal_note'] = this.cart.internal_note;
                    }

                    if(this.cart.shipping_address) {

                        if(this.cart.shipping_address.first_name) {
                            this.params['shipping_address_first_name'] = this.cart.shipping_address.first_name;
                        }
                        if(this.cart.shipping_address.last_name) {
                            this.params['shipping_address_last_name'] = this.cart.shipping_address.last_name;
                        }
                        if(this.cart.shipping_address.organisation_title) {
                            this.params['shipping_address_organisation_title'] = this.cart.shipping_address.organisation_title;
                        }
                        if(this.cart.shipping_address.premises) {
                            this.params['shipping_address_premises'] = this.cart.shipping_address.premises;
                        }
                        if(this.cart.shipping_address.line_1) {
                            this.params['shipping_address_line_1'] = this.cart.shipping_address.line_1;
                        }
                        if(this.cart.shipping_address.line_2) {
                            this.params['shipping_address_line_2'] = this.cart.shipping_address.line_2;
                        }
                        if(this.cart.shipping_address.locality) {
                            this.params['shipping_address_locality'] = this.cart.shipping_address.locality;
                        }
                        if(this.cart.shipping_address.subdivision) {
                            this.params['shipping_address_subdivision'] = this.cart.shipping_address.subdivision;
                        }
                        if(this.cart.shipping_address.postal_code) {
                            this.params['shipping_address_postal_code'] = this.cart.shipping_address.postal_code;
                        }
                        if(this.cart.shipping_address.country_id) {
                            this.params['shipping_address_country_id'] = this.cart.shipping_address.country_id;
                        }
                        if(this.cart.shipping_address.mobile_phone_number) {
                            this.params['shipping_address_mobile_phone_number'] = this.cart.shipping_address.mobile_phone_number;
                        }
                        if(this.cart.shipping_address.what3words_address) {
                            this.params['shipping_address_what3words_address'] = this.cart.shipping_address.what3words_address;
                        }
                        if(this.cart.shipping_address.courier_instructions) {
                            this.params['shipping_address_courier_instructions'] = this.cart.shipping_address.courier_instructions;
                        }

                    }

                    if(this.cart.shipping_notification_email_address) {
                        this.params['shipping_notification_email_address'] = this.cart.shipping_notification_email_address;
                    }

                    if(this.cart.shipping) {
                        this.params['shipping_price'] = this.cart.shipping.price;
                        this.params['shipping_discount_rate'] = 0;
                    }

                    if(this.cart.items) {

                        for(let i = 0; i < this.cart.items.length; i++) {

                            let item = this.cart.items[i];

                            let number = i + 1;

                            if(item.type == 'product') { // product
                                this.params[`item_${number}_product_id`] = item.id
                            }
                            else if(item.type == 'product_variant') { // product variant
                                this.params[`item_${number}_product_variant_id`] = item.id
                            }

                            this.params[`item_${number}_quantity`] = item.quantity
                            this.params[`item_${number}_sell_price`] = item.sell_price
                            this.params[`item_${number}_has_custom_price`] = item.has_custom_price;
                            this.params[`item_${number}_discount_rate`] = item.discount_rate;

                            if(item.customer_note) {
                                this.params[`item_${number}_customer_note`] = item.customer_note
                            }
                            else {
                                this.params[`item_${number}_customer_note`] = "";
                            }

                            if(item.internal_note) {
                                this.params[`item_${number}_internal_note`] = item.internal_note
                            }
                            else {
                                this.params[`item_${number}_internal_note`] = "";
                            }

                        }
                    }

                }).catch(() => { });

            }

        })
        .catch(() => { });

        this.observerService.subscribe('network_status_changed|quote_add_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to create or update a quote, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|quote_add_modal");
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading cart").then(() => {
                this.loadCart().then(() => {
                    this.loading_spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    loadCart() {
        return new Promise<void>((resolve, reject) => {
            this.cartService.get().then((cart) => {
                if(cart) {
                    this.cart = cart;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadQuote() {
        return new Promise<void>((resolve, reject) => {
            this.quote = null;
            this.quoteService.getQuote(this.cart.quote_id).then((quote) => {
                if(quote) {
                    this.quote = quote;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    processQuote() {

        let validated = true;

        if(this.cart.items) {

            const unsupported_item_types: Record<string, string> = {
                ticket_type: "Tickets",
                donation_campaign: "Donations",
                gift_voucher: "Gift vouchers"
            };

            for(let i = 0; i < this.cart.items.length; i++) {

                let item = this.cart.items[i];

                if(unsupported_item_types[item.type]) {
                    const item_name = unsupported_item_types[item.type];
                    this.presentAlert("Unsupported item", `${item_name} aren't supported when creating or updating a quote. Please remove any ${item_name.toLowerCase()} from the cart and try again.`);
                    validated = false;
                    break;
                }

            }
        }

        // NOTE: ignore repeat presses while the quote is being saved, otherwise a double tap/Enter creates it twice
        if(validated && !this.processing_quote) {

            this.processing_quote = true;

            if(this.params['expiry_date'] && this.params['expiry_date'].length) {
                this.params['expiry_date'] = this.moment(this.params['expiry_date']).utc().toISOString();
            }
            else {
                delete this.params['expiry_date'];
            }

            if(this.cart.quote_id) {
                this.presentSpinner("Updating quote").then(() => {
                    this.quoteService.updateQuote(this.cart.quote_id,this.params).then(() => {
                        this.cartService.reset().then(async () => {
                            this.router.navigateByUrl("/secure/quotes/" + this.cart.quote_id);
                            await this.spinner.dismiss();
                            await this.modalController.dismiss();
                        })
                        .catch(() => {
                            this.spinner.dismiss();
                            this.processing_quote = false;
                        });
                    })
                    .catch(() => {
                        this.spinner.dismiss();
                        this.processing_quote = false;
                    });
                });
            }
            else {
                this.presentSpinner("Creating quote").then(() => {
                    this.quoteService.addQuote(this.params).then((quote) => {
                        this.cartService.reset().then(async () => {
                            if(this.cart.parked_cart_id) {
                                this.parkedCartService.deleteParkedCart(this.cart.parked_cart_id);
                            }
                            this.router.navigateByUrl("/secure/quotes/" + quote['id']);
                            await this.spinner.dismiss();
                            await this.modalController.dismiss();
                        })
                        .catch(() => {
                            this.spinner.dismiss();
                            this.processing_quote = false;
                        });
                    })
                    .catch(() => {
                        this.spinner.dismiss();
                        this.processing_quote = false;
                    });
                });
            }

        }

    }

    async presentDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: (this.params['expiry_date'] ? this.params['expiry_date'] : ""),
                    format: "date-time",
                    clear_date: true
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    if(data.data == "date_deleted") {
                        this.params['expiry_date'] = "";
                        this.formatted_expiry_date = null;
                    }
                    else {
                        this.params['expiry_date'] = data.data['date'];
                        this.formatted_expiry_date = this.moment(this.params['expiry_date']).format("D MMM YYYY, HH:mm");
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}